import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PROFILE_VIEW } from "@/constants/testIds";
import { toast } from "sonner";
import { Heart, MessageCircle, BadgeCheck, MapPin, Briefcase, GraduationCap, Crown, Lock } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=900&q=80";

export default function ProfileView() {
    const { id } = useParams();
    const { user } = useAuth();
    const [p, setP] = useState(null);
    const [activePhoto, setActivePhoto] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        client.get(`/profiles/${id}`).then(({ data }) => setP(data)).catch(() => toast.error("Profile not found"));
    }, [id]);

    if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-navy-400">Loading…</div>;

    const photos = p.photos?.length ? p.photos : [FALLBACK];
    const img = photos[activePhoto];
    const imgSrc = img?.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`;

    const shortlist = async () => {
        try { await client.post(`/shortlist/${id}`); toast.success("Added to shortlist"); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const interest = async () => {
        try { await client.post(`/interests/${id}`); toast.success("Interest sent"); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const message = () => navigate(`/messages?to=${id}`);

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="grid lg:grid-cols-12 gap-10">
                {/* Photos */}
                <div className="lg:col-span-5">
                    <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-linen shadow-xl">
                        <img src={imgSrc} alt={p.full_name} className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = FALLBACK; }} />
                    </div>
                    {photos.length > 1 && (
                        <div className="grid grid-cols-5 gap-2 mt-3">
                            {photos.map((ph, i) => {
                                const src = ph.startsWith("http") ? ph : `${process.env.REACT_APP_BACKEND_URL}${ph}`;
                                return (
                                    <button key={i} onClick={() => setActivePhoto(i)}
                                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${i === activePhoto ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`}>
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="lg:col-span-7">
                    <div className="flex items-center gap-2 flex-wrap">
                        {p.verified && (
                            <div className="inline-flex items-center gap-1 bg-gold-100 text-gold-700 rounded-lg px-3 py-1 text-[10px] uppercase tracking-wider font-semibold">
                                <BadgeCheck className="h-3.5 w-3.5" /> Verified
                            </div>
                        )}
                        {p.premium && (
                            <div className="inline-flex items-center gap-1 bg-navy text-gold rounded-lg px-3 py-1 text-[10px] uppercase tracking-wider font-semibold">
                                <Crown className="h-3.5 w-3.5" /> Premium
                            </div>
                        )}
                        {p.denomination && (
                            <div className="bg-linen border border-navy-100 rounded-lg px-3 py-1 text-[10px] uppercase tracking-wider text-navy-600 font-semibold">{p.denomination}</div>
                        )}
                    </div>

                    <h1 className="mt-4 font-serif text-5xl text-navy tracking-tighter">{p.full_name || p.name}</h1>
                    <div className="mt-2 text-navy-500 font-sans">
                        {p.age ? `${p.age} years` : ""}
                        {p.age && p.height_cm ? " • " : ""}
                        {p.height_cm ? `${Math.floor(p.height_cm/30.48)}'${Math.round((p.height_cm/2.54)%12)}" (${p.height_cm} cm)` : ""}
                    </div>

                    {user && user.user_id !== id && (
                        <div className="mt-6 flex gap-3 flex-wrap">
                            <button onClick={shortlist} data-testid={PROFILE_VIEW.shortlist}
                                className="font-sans text-xs uppercase tracking-[0.18em] border border-navy-200 text-navy hover:border-navy hover:bg-navy hover:text-white rounded-lg px-6 py-3 transition-all active:scale-95 flex items-center gap-2">
                                <Heart className="h-4 w-4" /> Shortlist
                            </button>
                            <button onClick={interest} data-testid={PROFILE_VIEW.interest}
                                className="font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy hover:bg-gold-600 hover:text-white rounded-lg px-6 py-3 transition-all active:scale-95 shadow-md shadow-gold/20 flex items-center gap-2">
                                Express Interest
                            </button>
                            <button onClick={message} data-testid={PROFILE_VIEW.message}
                                className="font-sans text-xs uppercase tracking-[0.18em] bg-navy text-white hover:bg-navy-700 rounded-lg px-6 py-3 transition-all active:scale-95 flex items-center gap-2">
                                <MessageCircle className="h-4 w-4" /> Message
                            </button>
                        </div>
                    )}

                    {p.about_me && (
                        <div className="mt-10 bg-linen rounded-2xl p-6">
                            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">About</div>
                            <p className="mt-3 font-serif text-xl text-navy italic leading-relaxed">&ldquo;{p.about_me}&rdquo;</p>
                        </div>
                    )}

                    <div className="mt-8 grid sm:grid-cols-2 gap-4">
                        {p.profession && <DetailRow icon={<Briefcase />} label="Profession" value={p.profession} />}
                        {p.education && <DetailRow icon={<GraduationCap />} label="Education" value={p.education} />}
                        {(p.city || p.country) && <DetailRow icon={<MapPin />} label="Location" value={[p.city, p.country].filter(Boolean).join(", ")} />}
                        {p.marital_status && <DetailRow icon={<Heart />} label="Marital Status" value={p.marital_status} />}
                        {p.mother_tongue && <DetailRow icon={<></>} label="Mother Tongue" value={p.mother_tongue} />}
                        {p.annual_income && <DetailRow icon={<></>} label="Annual Income" value={p.annual_income} />}
                    </div>

                    {/* Contact (premium-locked) */}
                    <div className="mt-8 bg-white border border-navy-100 rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-serif text-2xl text-navy">Contact Details</h3>
                            {!p.email && <div className="text-xs uppercase tracking-wider text-gold-700 flex items-center gap-1"><Lock className="h-3 w-3" /> Premium</div>}
                        </div>
                        {p.email ? (
                            <div className="space-y-2 font-sans text-sm">
                                <div><span className="text-navy-400">Email:</span> <span className="text-navy">{p.email}</span></div>
                                {p.phone && <div><span className="text-navy-400">Phone:</span> <span className="text-navy">{p.phone}</span></div>}
                            </div>
                        ) : (
                            <p className="text-sm text-navy-500 font-sans">
                                Upgrade to Premium to view contact details. <a href="/premium" className="text-gold-700 underline">View plans</a>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function DetailRow({ icon, label, value }) {
    return (
        <div className="bg-white border border-navy-100 rounded-xl p-4 flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-linen text-gold-700 flex items-center justify-center shrink-0">{icon}</div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-navy-400 font-semibold">{label}</div>
                <div className="font-sans text-sm text-navy mt-0.5">{value}</div>
            </div>
        </div>
    );
}
